import { FC, useCallback, useState } from 'react';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import { Campaign, Search } from '@mui/icons-material';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { TPgCustomerListItem } from 'services/api/paymentGateway/types';
import { ESortDirection, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { notificationContainer } from 'utils/notificationContainer';
import { PgList } from '../common/PgList';
import { PgPage } from '../common/PgPage';
import { useReloadKey } from '../common/helpers';
import { DETAILS_BUTTON, YES_NO_FILTER, pgActionsColumn, pgColumn } from '../common/tableHelpers';
import { PG_UK_OVERRIDE_LABELS } from '../constants';
import { CustomerDialog } from './CustomerDialog';
import { RelinkCampaignDialog } from './RelinkCampaignDialog';

type TRow = TPgCustomerListItem;
const col = (
	name: string,
	selector: keyof TRow & string,
	type: ETableStructureType,
	extra: Partial<TTableStructure<keyof TRow, TRow>> = {},
) => pgColumn<TRow>(name, selector, type, extra);

const yesNo = (name: string, selector: keyof TRow & string, extra = {}) =>
	col(name, selector, ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		filter: YES_NO_FILTER,
		colors: [
			['true', 'success.light'],
			['false', 'warning.light'],
		],
		value: (item) => String(item[selector]),
		...extra,
	});

const STRUCTURE: TTableStructure<keyof TRow, TRow>[] = [
	col('Client', 'clientNumber', ETableStructureType.STRING, {
		sortable: true,
		filterable: true,
		searchable: true,
	}),
	col('Name', 'displayName', ETableStructureType.STRING, { sortable: true, filterable: true }),
	col('EM ID', 'emId', ETableStructureType.STRING, {
		sortable: true,
		value: (item) => String(item.emId),
	}),
	col('Country', 'registeredCountryCode', ETableStructureType.STRING, { sortable: true }),
	yesNo('UK', 'isUk'),
	col('UK override', 'ukOverride', ETableStructureType.STATUS, {
		sortable: true,
		filterable: true,
		filter: Object.entries(PG_UK_OVERRIDE_LABELS),
		colors: [
			['force_uk', 'info.light'],
			['force_not_uk', 'info.light'],
			['auto', 'success.light'],
		],
	}),
	yesNo('Active methods', 'hasActiveMethods'),
	yesNo('Needs re-link', 'needsRelink', {
		colors: [
			['true', 'warning.light'],
			['false', 'success.light'],
		],
	}),
	col('Created', 'createdAt', ETableStructureType.DATE_TIME, { sortable: true }),
	pgActionsColumn<TRow>([DETAILS_BUTTON]),
];

/**
 * Customers (03 §4.7): search by number or name, the customer card with payment methods, coverage,
 * UK override and manual charges, and the re-link campaign.
 */
export const Customers: FC = () => {
	const [customerId, setCustomerId] = useState<number | null>(null);
	const [clientNumber, setClientNumber] = useState('');
	const [finding, setFinding] = useState(false);
	const [campaignOpen, setCampaignOpen] = useState(false);
	const [reloadKey, reload] = useReloadKey();

	const handleAction = useCallback((row: TRow, action: string) => {
		if (action === DETAILS_BUTTON.name) setCustomerId(row.id);
	}, []);

	// By client number the gateway also creates a customer that is not known to it yet (from EAS).
	const find = async () => {
		const number = clientNumber.trim();
		if (!number) return;
		setFinding(true);
		try {
			const customer = await api.paymentGateway.getCustomerByClientNumber(number);
			setCustomerId(customer.id);
			reload();
		} catch (e) {
			notificationContainer(getPgErrorMessage(e, 'Customer not found'), 'error');
		} finally {
			setFinding(false);
		}
	};

	return (
		<PgPage
			title="Customers"
			actions={
				<Button
					size="small"
					variant="outlined"
					startIcon={<Campaign />}
					onClick={() => setCampaignOpen(true)}
				>
					Re-link campaign
				</Button>
			}
		>
			<PgList<TRow>
				title="Customers"
				structure={STRUCTURE}
				fetcher={api.paymentGateway.getCustomers}
				onAction={handleAction}
				defaultOrder={{ fieldName: 'createdAt', direction: ESortDirection.DESC }}
				reloadKey={reloadKey}
				errorText="Failed to load customers"
				headerContent={
					<Box sx={{ display: 'flex', gap: 1 }}>
						<TextField
							size="small"
							label="Open by client number"
							value={clientNumber}
							onChange={(e) => setClientNumber(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') void find();
							}}
							sx={{ backgroundColor: 'background.paper', minWidth: 200 }}
						/>
						<Button
							size="small"
							variant="contained"
							onClick={() => void find()}
							disabled={!clientNumber.trim() || finding}
							startIcon={finding ? <CircularProgress size={14} /> : <Search />}
						>
							Open
						</Button>
					</Box>
				}
			/>
			<CustomerDialog
				customerId={customerId}
				onClose={() => setCustomerId(null)}
				onChanged={reload}
			/>
			<RelinkCampaignDialog open={campaignOpen} onClose={() => setCampaignOpen(false)} />
		</PgPage>
	);
};
